import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { DELETE_DOMAIN } from "../../queries";
import { getOperationName } from "@apollo/client/utilities";
import { LyfeTooltip } from "@lyfegen-platform/react-common";
import { Mutation, Query } from "../../../../../models/generated/graphql";
import { useHistory } from "react-router";
import useOrganizationStyles from "../../use-organization-styles";
import BottomErrorBanner from "apps/platform-client/client/src/components/errorMessages/BottomErrorBanner";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { getNetworkErrorStatusCode } from "../../../../../components/SentryLog";
import { DomainLevel } from "apps/platform-client/client/src/models/enums/enums";
import { DomainType } from "./DomainCard";
import { useModelsMutation } from "apps/platform-client/client/src/hooks/useModelsModule";

interface IDeleteButtonProps {
	id: string;
	currentDomain: DomainType | undefined;
	setIsDeleting: Dispatch<SetStateAction<boolean>>;
}

export const DeleteButton = (props: IDeleteButtonProps) => {
	const { currentDomain } = props;
	const [mutation, { error }] = useModelsMutation(DELETE_DOMAIN);
	const { t } = useTranslation();
	const { push } = useHistory();
	const { deleteDomainBtn } = useOrganizationStyles();
	const [isOpenErrorBanner, setIsOpenErrorBanner] = useState(false);
	const handleDelete = () => {
		props.setIsDeleting(true);
		mutation({
			variables: { id: props.id },
			onCompleted: () => {
				push("/organization-settings/domains");
			},
			onError: (error) => {
				console.error("Error deleting domain:", error);
			},
		});
	};

	useEffect(() => {
		error ? setIsOpenErrorBanner(true) : setIsOpenErrorBanner(false);
	}, [error]);

	const hasChildrenWithSimulationsOrUsers = () => {
		if (currentDomain?.children?.length) {
			return currentDomain?.children.some((subdomain) => subdomain.simulationCount > 0 || subdomain.users.length);
		}

		return false;
	};

	const getTooltip = () => {
		if (currentDomain?.level === DomainLevel.GLOBAL) {
			return "domain-delete-disabled-global";
		}

		return currentDomain?.level == DomainLevel.PRODUCT
			? "domain-delete-disabled-product-domain"
			: "domain-delete-disabled-product-subdomain";
	};

	const isDisabled =
		!!currentDomain?.users.length ||
		currentDomain?.level == DomainLevel.GLOBAL ||
		!!(currentDomain?.simulationCount && currentDomain.simulationCount > 0) ||
		hasChildrenWithSimulationsOrUsers();

	const deleteDomainTooltip = isDisabled ? getTooltip() : "";

	return (
		<>
			<LyfeTooltip title={t(deleteDomainTooltip)}>
				<span>
					<Button className={deleteDomainBtn} disabled={isDisabled} variant="outlined" onClick={handleDelete}>
						{t("domain-delete")}
					</Button>
				</span>
			</LyfeTooltip>
			{isOpenErrorBanner && (
				<BottomErrorBanner
					statusCode={getNetworkErrorStatusCode(error?.networkError)}
					isOpen={isOpenErrorBanner}
					onClose={() => setIsOpenErrorBanner(false)}
					errorTitle={t("simulator-delete-domain-error-p1")}
					secondaryText={t("simulator-delete-domain-error-p2")}
					handleTryAgain={() => handleDelete()}
					error={error}
					operation={getOperationName(DELETE_DOMAIN) as keyof Mutation | keyof Query}
				/>
			)}
		</>
	);
};
